import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { adminAPI } from '../../api/client';
import { Card, Badge, Modal, Alert, Btn, FormInput, FormSelect, Spinner, EmptyState, Avatar, fmt, fmtDate } from '../../components/UI';

const STATUS_OPTS = [
  { value: 'completed', label: '✅ Available Balance (Ready to Withdraw)' },
  { value: 'pending',   label: '⏳ Pending Earnings (Being Processed)'    },
  { value: 'paid',      label: '💰 Total Paid Out (Withdrawn)'            },
];

export default function AdminEarnings() {
  const [page, setPage]   = useState(1);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving,    setSaving]    = useState(false);
  const [modalError, setModalError] = useState('');
  const [success,    setSuccess]    = useState('');

  const fetchEarnings = useCallback(() => adminAPI.earnings(page), [page]);
  const { data, loading, error, refetch } = useFetch(fetchEarnings);
  const earnings   = data?.earnings   || [];
  const pagination = data?.pagination || {};

  function openEdit(e) {
    setEditItem(e);
    setEditForm({ platform_name: e.platform_name, amount_earned: e.amount_earned, user_share: e.user_share, status: e.status, notes: e.notes || '' });
    setModalError('');
  }

  async function saveEdit(e) {
    e.preventDefault();
    setSaving(true); setModalError('');
    try {
      await adminAPI.updateEarning(editItem.id, editForm);
      setSuccess('Earnings record updated.');
      setEditItem(null);
      refetch();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update.');
    } finally { setSaving(false); }
  }

  async function deleteEarning(item) {
    if (!window.confirm(`Delete this earnings record?\n\n${item.user_name} · ${item.platform_name} · $${parseFloat(item.user_share).toFixed(2)}\n\nThis cannot be undone.`)) return;
    try {
      await adminAPI.deleteEarning(item.id);
      setSuccess('Earnings record deleted.');
      refetch();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete earnings record.');
    }
  }

  const ef = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Earnings Records</h1>
          <p style={styles.sub}>All earning entries across every user and platform. Edit status or amounts as needed.</p>
        </div>
      </div>

      {success && <Alert type="success" message={success} />}

      <Card>
        {loading ? <Spinner /> : error ? <p style={{ color: '#dc2626' }}>{error}</p>
          : earnings.length === 0 ? <EmptyState icon="💰" message="No earnings records yet." />
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['User','Platform','Total Earned','Current Earnings','Status','Notes','Date','Actions'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {earnings.map(e => (
                    <tr key={e.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar name={e.user_name} size={28} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f5e' }}>{e.user_name}</div>
                            <div style={{ fontSize: 11, color: '#9099b4' }}>{e.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: pColor(e.platform_name) }} />
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{e.platform_name}</span>
                        </div>
                      </td>
                      <td style={styles.td}><span style={{ color: '#636b89', fontSize: 13 }}>{fmt(e.amount_earned)}</span></td>
                      <td style={styles.td}><strong style={{ color: '#16a34a' }}>{fmt(e.user_share)}</strong></td>
                      <td style={styles.td}><Badge status={e.status} /></td>
                      <td style={styles.td}><span style={{ fontSize: 12, color: '#9099b4', maxWidth: 120, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.notes || '—'}</span></td>
                      <td style={styles.td}><span style={{ fontSize: 12, color: '#9099b4' }}>{fmtDate(e.created_at)}</span></td>
                      <td style={styles.td}>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          <Btn variant="ghost" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => openEdit(e)}>✏️ Edit</Btn>
                          <Btn variant="ghost" style={{ fontSize: 12, padding: '5px 10px', color:'#dc2626' }} onClick={() => deleteEarning(e)}>🗑 Delete</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }

        {pagination.total > pagination.limit && (
          <div style={styles.pagination}>
            <button style={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ fontSize: 13, color: '#636b89' }}>Page {page} of {Math.ceil(pagination.total / pagination.limit)}</span>
            <button style={styles.pageBtn} disabled={page * pagination.limit >= pagination.total} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      <Modal open={!!editItem} onClose={() => setEditItem(null)} title="Edit Earnings Record">
        <Alert type="error" message={modalError} />
        {editItem && (
          <div style={{ background: '#f8f9fc', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
            <strong>{editItem.user_name}</strong> · {editItem.platform_name}
          </div>
        )}
        <div style={{ background:'#eef1ff', borderRadius:10, padding:'11px 14px', marginBottom:16, fontSize:12, color:'#4338ca', lineHeight:1.55 }}>
          ℹ️ Changing the balance below moves this record's <strong>Current Earnings</strong> between the user's dashboard balances.
        </div>
        <form onSubmit={saveEdit}>
          <FormInput label="Platform Name" value={editForm.platform_name || ''} onChange={ef('platform_name')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormInput label="Total Earned ($)"     type="number" min="0" step="0.01" value={editForm.amount_earned || ''} onChange={ef('amount_earned')} />
            <FormInput label="Current Earnings ($)" type="number" min="0" step="0.01" value={editForm.user_share || ''}    onChange={ef('user_share')} />
          </div>
          <FormSelect label="Add to which balance?" options={STATUS_OPTS} value={editForm.status || 'pending'} onChange={ef('status')} />
          <FormInput label="Notes" placeholder="Optional notes" value={editForm.notes || ''} onChange={ef('notes')} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="outline" type="button" onClick={() => setEditItem(null)}>Cancel</Btn>
            <Btn loading={saving} type="submit">Save Changes</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

const pColor = n => ({ 'Appen':'#ff6b35','Scale AI':'#4f6ef7','Remotasks':'#16a34a','Toloka':'#f4a61d','Prolific':'#7c3aed','Outlier AI':'#0891b2' }[n] || '#9099b4');

const styles = {
  wrap:       { padding: '32px', fontFamily: "'DM Sans',sans-serif" },
  header:     { marginBottom: 24 },
  h1:         { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', margin: 0 },
  sub:        { fontSize: 14, color: '#9099b4', marginTop: 4 },
  table:      { width: '100%', borderCollapse: 'collapse', minWidth: 800 },
  th:         { background: '#f8f9fc', padding: '11px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#9099b4', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #e2e6f0' },
  tr:         { borderBottom: '1px solid #f0f2f8' },
  td:         { padding: '12px 14px', fontSize: 14, verticalAlign: 'middle' },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, paddingTop: 16 },
  pageBtn:    { padding: '7px 16px', border: '1.5px solid #e2e6f0', borderRadius: 8, background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#636b89' },
};
